import React, { useState } from 'react';
import { Mail, Clock, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitContact } from '../services/api';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert projectType to project_type for backend
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType,
        message: formData.message
      };
      
      const response = await submitContact(submitData);
      
      toast({
        title: 'Message Sent!',
        description: response.message || 'Thank you for reaching out. We\'ll get back to you soon.',
        duration: 5000
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] pt-20">
      {/* Header Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-bold text-[#f5f5f5] mb-4 tracking-wider text-center">
          Get In <span className="text-[#4b63eb]">Touch</span>
        </h1>
        <p className="text-[#a0a0a0] text-lg text-center mb-16">
          Let's discuss how we can bring your architectural vision to life
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#f5f5f5] mb-2 tracking-wider uppercase">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d3748] text-[#f5f5f5] focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] transition-colors duration-300"
                  placeholder="Your name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#f5f5f5] mb-2 tracking-wider uppercase">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d3748] text-[#f5f5f5] focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] transition-colors duration-300"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#f5f5f5] mb-2 tracking-wider uppercase">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d3748] text-[#f5f5f5] focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] transition-colors duration-300"
                  placeholder="(904) 123-4567"
                />
              </div>

              {/* Project Type */}
              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-[#f5f5f5] mb-2 tracking-wider uppercase">
                  Project Type Optional
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d3748] text-[#f5f5f5] focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] transition-colors duration-300"
                >
                  <option value="">Select a project type</option>
                  <option value="residential-project">Residential Project</option>
                  <option value="multi-family-project">Multi Family Project</option>
                  <option value="commercial-project">Commercial Project</option>
                  <option value="industrial-project">Industrial Project</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#f5f5f5] mb-2 tracking-wider uppercase">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#2d3748] text-[#f5f5f5] focus:border-[#4b63eb] focus:outline-none focus:ring-1 focus:ring-[#4b63eb] transition-colors duration-300 resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5] px-8 py-4 text-sm tracking-widest uppercase font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#4b63eb]/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="order-1 lg:order-2 space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-[#f5f5f5] mb-6 tracking-wider">
                Let's Create Something <span className="text-[#4b63eb]">Amazing</span>
              </h2>
              <p className="text-[#a0a0a0] leading-relaxed mb-8">
                Whether you're an architect looking to showcase your latest project, a developer needing professional property photography, or a homeowner wanting to capture your beautiful space, we're here to help.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-[#1a1a1a] border border-[#2d3748]">
                  <MapPin size={24} className="text-[#4b63eb]" />
                </div>
                <div>
                  <h3 className="text-[#f5f5f5] font-semibold mb-1">Location</h3>
                  <p className="text-[#a0a0a0]">Jacksonville, Florida</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-[#1a1a1a] border border-[#2d3748]">
                  <Mail size={24} className="text-[#4b63eb]" />
                </div>
                <div>
                  <h3 className="text-[#f5f5f5] font-semibold mb-1">Email</h3>
                  <p className="text-[#a0a0a0]">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="p-3 bg-[#1a1a1a] border border-[#2d3748]">
                  <Clock size={24} className="text-[#4b63eb]" />
                </div>
                <div>
                  <h3 className="text-[#f5f5f5] font-semibold mb-1">Hours</h3>
                  <p className="text-[#a0a0a0]">Available Monday - Friday, 9am - 5pm EST</p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-r from-[#4b63eb]/10 to-[#3b4ec9]/10 border border-[#4b63eb]/30 p-6 mt-8">
              <h3 className="text-[#f04848] font-semibold mb-2">Prompt Communication</h3>
              <p className="text-[#f5f5f5] text-sm">
                Prompt communication is extremely important to us. For urgent requests please mention that and any details in your message.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
